// The arrival's arithmetic (R163, mockup round seven): what plays, how long
// it plays for, the easings the styles share, and the hex lattice Cell Ripple
// walks.
//
// None of it draws anything. The split keeps the rules testable without a
// renderer, and it lets a second renderer play the same arrival without
// importing the layer that owns the sprites (`fast/nopixi.test.ts`).

import {
  LATE_LEAD_SEC, RESYNC_FADE_SEC, SPAWN_MAX_CELLS as MAX_CELLS,
  SPAWN_PLAY_SEC as GEN_SPAWN_PLAY_SEC,
} from '../gen/presentation';
import type { SpawnStyle } from '../ui/settings';
import { clamp01, lerp } from './recipe-kit';

/** Which arrival this is. `resync` is a re-`Hello` inside the same season. */
export type SpawnKind = 'login' | 'season' | 'resync';

/** A style, plus the plain fade that reduced motion and a resync play. */
export type SpawnPlayed = SpawnStyle | 'fade';

// --- the mockup's own timings (round seven, "play" on each card), from
// presentation.toml (R312) ------------------------------------------------
export const SPAWN_PLAY_SEC: Record<SpawnPlayed, number> = GEN_SPAWN_PLAY_SEC;

/** The fade a resync plays: shorter still, because nothing was torn down
 *  that the player did not already have on screen. */
export { RESYNC_FADE_SEC };

/** How long before a run's own end `onLate` fires. The chrome dock-in
 *  (R182) hangs off it: at this point every style has revealed most of the
 *  map, so the panels travel while the spawn finishes the far edge instead
 *  of after it. A run shorter than the lead fires on its first tick. */
export { LATE_LEAD_SEC };

/** Cells past this many are not drawn: Cell Ripple grows its hex until the
 *  screen holds no more than this — the grid, one size up, never a
 *  different geometry. The figure rides presentation.toml
 *  [presentation.spawn]. */
export { MAX_CELLS };

// --- pure helpers (unit-tested; no DOM, no engine) ------------------------

export function easeOut(u: number): number {
  return 1 - Math.pow(1 - u, 3);
}

export function easeInOut(u: number): number {
  return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
}

export interface SpawnPlan {
  played: SpawnPlayed;
  playSec: number;
}

/**
 * Picks what actually plays. Two rules override the setting:
 *
 * - **reduced motion** plays the plain fade, at every style. The arrivals
 *   sweep, ripple and shake the whole canvas, which is exactly the motion
 *   the setting asks a page to drop.
 * - **a resync** plays a shorter fade. A stage-1 seaquake rebuild and an
 *   admin reseed both re-`Hello` inside the same season; the player is
 *   looking at that map already, so a three-second arrival there reads as
 *   a fault rather than as an entrance.
 */
export function planSpawn(style: SpawnStyle, kind: SpawnKind, reduced: boolean): SpawnPlan {
  if (reduced) return { played: 'fade', playSec: SPAWN_PLAY_SEC.fade };
  if (kind === 'resync') return { played: 'fade', playSec: RESYNC_FADE_SEC };
  return { played: style, playSec: SPAWN_PLAY_SEC[style] ?? SPAWN_PLAY_SEC['sonar-bloom'] };
}

export interface LatticeCell {
  x: number;
  y: number;
  /** Distance from the ripple's centre, 0 at the centre and 1 at the far
   *  corner. The ripple front is a threshold on this. */
  d: number;
}

export interface Lattice {
  size: number;
  cells: LatticeCell[];
}

/**
 * The hex centres that cover a `w` x `h` screen, on the map's own pointy-top
 * lattice (column pitch √3·s, row pitch 1.5·s, odd rows offset half a
 * column), aligned so that `(ox, oy)` is a cell centre.
 *
 * `size` grows until the count is under `MAX_CELLS`, so the cost of Cell
 * Ripple is bounded whatever the zoom.
 */
export function hexLattice(
  w: number, h: number, size: number, ox: number, oy: number, cap = MAX_CELLS,
): Lattice {
  let s = Math.max(4, size);
  // The test counts the real rows and columns, margin rows included, so the
  // lattice never holds more cells than the cap (R1344).
  for (let guard = 0; guard < 12; guard++) {
    if (latticeCount(w, h, s, ox, oy) <= cap) break;
    s *= 1.3;
  }
  const cw = Math.sqrt(3) * s, ch = 1.5 * s;
  const cells: LatticeCell[] = [];
  const cx = w / 2, cy = h / 2;
  const maxd = Math.max(1, Math.hypot(w / 2, h / 2));
  const r0 = Math.floor((-oy - s) / ch), r1 = Math.ceil((h - oy + s) / ch);
  for (let r = r0; r <= r1; r++) {
    const y = oy + r * ch;
    const xoff = (((r % 2) + 2) % 2) === 1 ? cw / 2 : 0;
    const c0 = Math.floor((-ox - xoff - cw) / cw), c1 = Math.ceil((w - ox - xoff + cw) / cw);
    for (let c = c0; c <= c1; c++) {
      const x = ox + xoff + c * cw;
      cells.push({ x, y, d: Math.hypot(x - cx, y - cy) / maxd });
    }
  }
  return { size: s, cells };
}

/** How many cells `hexLattice` lays at size `s`: the same rows and columns. */
function latticeCount(w: number, h: number, s: number, ox: number, oy: number): number {
  const cw = Math.sqrt(3) * s, ch = 1.5 * s;
  const r0 = Math.floor((-oy - s) / ch), r1 = Math.ceil((h - oy + s) / ch);
  let n = 0;
  for (let r = r0; r <= r1; r++) {
    const xoff = (((r % 2) + 2) % 2) === 1 ? cw / 2 : 0;
    const c0 = Math.floor((-ox - xoff - cw) / cw), c1 = Math.ceil((w - ox - xoff + cw) / cw);
    n += c1 - c0 + 1;
  }
  return n;
}

/** Blends two packed colours. Used for the arriving cell's rim. */
export function mixColor(a: number, b: number, t: number): number {
  const k = clamp01(t);
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(lerp(ar, br, k));
  const g = Math.round(lerp(ag, bg, k));
  const bl = Math.round(lerp(ab, bb, k));
  return (r << 16) | (g << 8) | bl;
}
